import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Progress,
  Typography
} from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import SeeWardLogo from '../../../assets/image/logoSeeWard.png';
import CodeInput from '../../UI/input/CodeInput';
import useCountdown, { formatCountdown } from '../../hooks/useCountdown';
import {
  forgotPassword,
  forgotPasswordConfirm,
  forgotPasswordVerify,
  messageFor,
  statusOf,
  TOO_MANY_REQUESTS_HOURLY,
  UNAVAILABLE
} from '../../../service/mfa.service';

/**
 * Locked-out account recovery, in three steps.
 *
 * This page used to say "ask an administrator", because it had to: every password endpoint
 * required a bearer token, so none of them was reachable by the one person who needs them.
 * Three unauthenticated routes now exist and this is the flow over them.
 *
 * All three steps live in one component on purpose. The reset ticket is single-use, lives five
 * minutes, and must never touch `localStorage` or the URL. Keeping the steps together keeps it
 * in component state, where a closed tab is the end of it.
 */

type Step = 'email' | 'code' | 'password';

/** The mailed code lives ten minutes; the ticket it buys lives five. */
const CODE_TTL_SECONDS = 600;
const RESEND_COOLDOWN_SECONDS = 60;
const TICKET_TTL_FALLBACK_SECONDS = 300;

const NO_ACCOUNT = 'No account exists for that address.';

/**
 * There is no self-service sign-up here: an account exists because somebody was invited to one,
 * so "create one" is not an action this screen can offer. `/contact` is no help either, since
 * it resolves to the signed-in catch-all and this reader cannot sign in. A mail draft with no
 * recipient is the one thing that works from a locked-out browser: the user addresses it to
 * whoever runs their workspace.
 */
const REQUEST_INVITE_MAILTO =
  'mailto:?subject=' +
  encodeURIComponent('SeeWard account request') +
  '&body=' +
  encodeURIComponent(
    'Hello,\n\nI tried to reset my SeeWard password and the page says there is no account ' +
      'for my address. Could you send me an invitation?\n\nThank you.'
  );

const scorePassword = (value: string): number => {
  if (!value) return 0;
  let score = 0;
  if (value.length >= 8) score += 1;
  if (value.length >= 12) score += 1;
  if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score += 1;
  if (/\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  return score;
};

const STRENGTH = [
  { 
    label: 'Very weak', 
    color: '#ff4d4f' 
  }, { 
    label: 'Weak', 
    color: '#ff7a45' 
  }, { 
    label: 'Fair', 
    color: '#faad14' 
  }, { 
    label: 'Good', 
    color: '#52c41a' 
  }, { 
    label: 'Strong', 
    color: '#389e0d' 
  }, { 
    label: 'Strong', 
    color: '#389e0d' 
  }
];

const ForgotPasswordFeature = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  // Component state only. Never localStorage, never the URL.
  const [resetTicket, setResetTicket] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [password, setPassword] = useState('');

  /** The address the server says holds no account. Belongs under the field, not in a banner. */
  const [unknownAddress, setUnknownAddress] = useState<string | null>(null);
  /**
   * A 503 and the address to retry it with. Held apart from `error` because it is the one
   * failure here that is neither the reader's fault nor final, so it is drawn as a transient
   * banner with a retry rather than as a red verdict on what they typed.
   */
  const [unavailable, setUnavailable] = useState<{
    message: string;
    address: string;
  } | null>(null);

  const codeExpiry = useCountdown(0);
  const resendCooldown = useCountdown(0);
  const ticketExpiry = useCountdown(0);

  const [passwordForm] = Form.useForm();

  const goToStart = (reason: string) => {
    setStep('email');
    setCode('');
    setResetTicket('');
    setError(reason);
    setNotice(null);
    setUnknownAddress(null);
    setUnavailable(null);
    codeExpiry.stop();
    ticketExpiry.stop();
  };

  /**
   * STEP 1, and the resend that repeats it.
   *
   * This used to advance on anything that was not an exception, because the route answered a
   * constant 202 to every address, real or invented. It does not any more, and the 404 is the
   * reason this screen changed: somebody who mistyped their address was sent on to a code
   * screen to wait for mail that was never going to arrive.
   *
   * The three failures stay apart, because the reader has to do a different thing for each.
   *
   * - **404**: what was typed is wrong. It goes under the field, and the step does not advance.
   * - **503**: the account lookup failed, which is not an answer about the address. It must not
   *   read like the 404: the account may well exist, so it gets a retry rather than a verdict.
   * - **429**: the hourly limit for this network, which correcting the address will not fix.
   */
  const requestCode = async (address: string, mode: 'first' | 'resend') => {
    setIsSubmitting(true);
    setError(null);
    setNotice(null);
    setUnknownAddress(null);
    setUnavailable(null);
    try {
      await forgotPassword(address);
      setEmail(address);
      setCode('');
      setStep('code');
      if (mode === 'resend') {
        setNotice(`A new code has been sent to ${address}.`);
      }
      codeExpiry.restart(CODE_TTL_SECONDS);
      resendCooldown.restart(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      const status = statusOf(err);
      if (status === 404) {
        // Back to the field even from a resend. Whatever this address was five minutes ago,
        // there is no account behind it now, and the code step has nothing left to offer.
        setStep('email');
        setUnknownAddress(address);
        codeExpiry.stop();
        resendCooldown.stop();
        return;
      }
      if (status === 503) {
        setUnavailable({ 
          message: messageFor(err, {}, UNAVAILABLE), 
          address 
        });
        return;
      }
      setError(
        messageFor(err, {
          429: TOO_MANY_REQUESTS_HOURLY,
          422: 'Please enter a valid email address.'
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestCode = (values: { email: string }) =>
    requestCode(values.email.trim(), 'first');

  // The server ignores a resend inside its own 60-second cooldown and still answers 202, so the
  // button is disabled for the same 60 seconds rather than sending a request that does nothing.
  const handleResend = () => requestCode(email, 'resend');

  // STEP 2. Five attempts, then the request is burned and the *correct* code stops working
  // too. A 429 here is terminal for this code, so it returns to step 1 instead of looping.
  const handleVerifyCode = async (submitted: string) => {
    setIsSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const { data } = await forgotPasswordVerify(email, submitted);
      setResetTicket(data.reset_ticket);
      ticketExpiry.restart(data.expires_in || TICKET_TTL_FALLBACK_SECONDS);
      codeExpiry.stop();
      setStep('password');
    } catch (err: any) {
      if (statusOf(err) === 429) {
        goToStart('Too many incorrect attempts. Request a new code.');
        return;
      }
      setCode('');
      setError(
        messageFor(err, {
          400: 'That code is not right, or it has expired.',
          422: 'Enter the six digits from the email.'
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // STEP 3. On success every existing session for the account is revoked, which is worth
  // telling the user rather than letting them discover it on their other devices.
  const handleConfirm = async (values: {
    password: string;
    confirm_password: string;
  }) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await forgotPasswordConfirm(
        resetTicket,
        values.password,
        values.confirm_password
      );
      navigate('/login', {
        replace: true,
        state: {
          notice:
            'Your password is updated. You have been signed out everywhere. Sign in with your new password.'
        }
      });
    } catch (err: any) {
      const detail = String(err?.response?.data?.detail ?? '');
      if (detail.toLowerCase().includes('ticket')) {
        goToStart('That reset request has expired. Request a new code.');
        return;
      }
      setError(
        messageFor(err, {
          400: 'That password was rejected. It may not meet the password policy.',
          422: 'Your password must be at least 8 characters.'
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const strength = STRENGTH[scorePassword(password)];

  /**
   * The 404, drawn where the mistake is rather than in a banner at the top of the card. It
   * carries the way out with it: this reader cannot create an account, so the only move left
   * is asking the person who can.
   */
  const renderUnknownAddress = () => (
    <span>
      {NO_ACCOUNT} Check the spelling, or{' '}
      <a href={REQUEST_INVITE_MAILTO}>ask an administrator to invite you</a>.
    </span>
  );

  const renderEmailStep = () => (
    <>
      <Typography.Title level={4} className="!mb-1">
        Reset your password
      </Typography.Title>
      <Typography.Paragraph type="secondary" className="text-[13px]">
        Enter the address you sign in with and we send a six-digit code to it.
      </Typography.Paragraph>
      <Form 
        layout="vertical" 
        onFinish={handleRequestCode} 
        initialValues={{ email }}
      >
        <Form.Item
          name="email"
          label="Email"
          validateStatus={unknownAddress ? 'error' : undefined}
          // `help` outranks the rule messages for as long as it is set, which is why the
          // keystroke below clears it: "please enter a valid email address" has to be able to
          // come back the moment the field is being edited again.
          help={unknownAddress ? renderUnknownAddress() : undefined}
          rules={[
            { 
              required: true, 
              message: 'Please enter your email address' 
            }, { 
              type: 'email', 
              message: 'Please enter a valid email address' 
            }
          ]}
        >
          <Input
            size="large"
            autoFocus
            prefix={<MailOutlined className="text-gray-400" />}
            placeholder="you@example.com"
            autoComplete="email"
            onChange={() => setUnknownAddress(null)}
          />
        </Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={isSubmitting}
        >
          Send code
        </Button>
      </Form>
    </>
  );

  const renderCodeStep = () => (
    <>
      <Typography.Title level={4} className="!mb-1">
        Enter your code
      </Typography.Title>
      <Typography.Paragraph type="secondary" className="text-[13px]">
        We sent a six-digit code to <strong>{email}</strong>. It expires in{' '}
        {formatCountdown(codeExpiry.secondsLeft)}.
      </Typography.Paragraph>

      <div className="flex justify-center my-6">
        <CodeInput
          value={code}
          onChange={setCode}
          onComplete={handleVerifyCode}
          disabled={isSubmitting}
          autoFocus
          status={error ? 'error' : undefined}
        />
      </div>

      {codeExpiry.secondsLeft === 0 && (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="That code has expired. Send a new one to continue."
        />
      )}

      <div className="flex items-center justify-between gap-2">
        <Button type="link" className="!px-0" onClick={() => goToStart('')}>
          Use a different address
        </Button>
        <Button
          onClick={handleResend}
          disabled={resendCooldown.isRunning || isSubmitting}
        >
          {resendCooldown.isRunning
            ? `Resend in ${resendCooldown.secondsLeft}s`
            : 'Resend code'}
        </Button>
      </div>
    </>
  );

  const renderPasswordStep = () => (
    <>
      <Typography.Title level={4} className="!mb-1">
        Set a new password
      </Typography.Title>
      <Typography.Paragraph type="secondary" className="text-[13px]">
        Choose a password of at least 8 characters. You have{' '}
        {formatCountdown(ticketExpiry.secondsLeft)} to finish.
      </Typography.Paragraph>

      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="Saving this signs you out everywhere"
        description={
          <span className="text-[13px]">
            Every device and browser currently signed in to this account is signed out. You
            sign in again with the new password.
          </span>
        }
      />

      <Form
        form={passwordForm}
        layout="vertical"
        onFinish={handleConfirm}
        autoComplete="off"
      >
        <Form.Item
          name="password"
          label="New password"
          rules={[
            { 
              required: true, 
              message: 'Please enter a new password' 
            }, { 
              min: 8, 
              message: 'Your password must be at least 8 characters' 
            }
          ]}
        >
          <Input.Password
            size="large"
            autoFocus
            autoComplete="new-password"
            prefix={<LockOutlined className="text-gray-400" />}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Form.Item>

        {password.length > 0 && (
          <div className="-mt-3 mb-4">
            <Progress
              percent={(scorePassword(password) / 5) * 100}
              showInfo={false}
              size="small"
              strokeColor={strength.color}
            />
            <Typography.Text type="secondary" className="text-[12px]">
              Password strength: {strength.label}
            </Typography.Text>
          </div>
        )}

        <Form.Item
          name="confirm_password"
          label="Confirm new password"
          dependencies={['password']}
          rules={[
            { 
              required: true, 
              message: 'Please confirm your new password' 
            }, ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Both passwords must match'));
              }
            })
          ]}
        >
          <Input.Password
            size="large"
            autoComplete="new-password"
            prefix={<LockOutlined className="text-gray-400" />}
          />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={isSubmitting}
        >
          Update password
        </Button>
      </Form>
    </>
  );

  return (
    <div 
      className="flex min-h-screen flex-col justify-center items-center px-4 py-10"
    >
      <div className="w-full max-w-[480px]">
        <div className="flex justify-center mb-8">
          <a href="/">
            <img alt="SeeWard" src={SeeWardLogo} className="h-10 w-auto" />
          </a>
        </div>

        <Card>
          {error && (
            <Alert
              type="error"
              showIcon
              className="mb-4"
              message={error}
              closable
              onClose={() => setError(null)}
            />
          )}
          {unavailable && (
            <Alert
              type="warning"
              showIcon
              className="mb-4"
              message="We could not send a code just now"
              description={
                <div className="text-[13px]">
                  <div className="mb-3">
                    {unavailable.message} This is a fault on our side, not a verdict on the
                    address: no code has gone out, and the account may well exist.
                  </div>
                  <Button
                    size="small"
                    loading={isSubmitting}
                    onClick={() =>
                      requestCode(
                        unavailable.address,
                        step === 'code' ? 'resend' : 'first'
                      )
                    }
                  >
                    Try again
                  </Button>
                </div>
              }
            />
          )}
          {notice && (
            <Alert
              type="success"
              showIcon
              className="mb-4"
              message={notice}
              closable
              onClose={() => setNotice(null)}
            />
          )}

          {step === 'email' && renderEmailStep()}
          {step === 'code' && renderCodeStep()}
          {step === 'password' && renderPasswordStep()}
        </Card>

        <div className="flex justify-center mt-6">
          <Button type="link" onClick={() => navigate('/login')}>
            Back to sign in
          </Button>
        </div>

        <Typography.Paragraph
          type="secondary"
          className="text-center text-[13px] mt-6"
        >
          <LockOutlined className="mr-2" />
          Never share your SeeWard credentials. If other people need access, invite them as
          members of your team from the settings.
        </Typography.Paragraph>
      </div>
    </div>
  );
};

export default ForgotPasswordFeature;
